import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  ReadonlyWatchable,
  TargetableModel,
  useWatchableValue,
  Watchable,
} from '../../actions/framework';
import { Modal, Text } from '@cotera/client/app/components/ui';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Button } from '../ui/button';
import { TabIndicator } from '../topbar/tab';
import { Assert } from '@cotera/utilities';
import { useTabManager, useTabParams } from '../../tab-manager';
import { config } from '@cotera/client/config/config';

const noop = () => false;

const NavigationBlocker: React.FC<{
  children: React.ReactNode;
  shouldBlock: () => boolean;
  message: string;
}> = ({ children, shouldBlock: userShouldBlock, message }) => {
  const context = useContext(NavigationBlockerContext);

  const shouldBlock = config.allowNavigationBlocker ? userShouldBlock : noop;

  Assert.assert(context !== null);

  const { showDialog, pendingLocation } = context;

  const shouldBlockRef = useRef(shouldBlock);
  const historyStateAdded = useRef(false);

  // Update ref when shouldBlock changes
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
  }, [shouldBlock]);

  // Block browser refresh/close/back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current()) {
        e.preventDefault();
        e.returnValue =
          message ||
          'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [message]);

  // Block browser back/forward navigation
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();

      const pathname = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;

      if (!shouldBlockRef.current() || pendingLocation.snapshot() === 'back') {
        //-2 because we have the "buffered" history entry
        window.history.go(-2);
        return;
      }

      window.history.pushState({ blocked: true }, '', pathname + search + hash);

      // Show confirmation dialog
      showDialog.set('back');
    };

    // Add a "buffered" history entry when we start blocking
    if (!historyStateAdded.current) {
      window.history.pushState(
        { blocked: true },
        '',
        window.location.pathname + window.location.search + window.location.hash
      );
    }
    historyStateAdded.current = true;

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      historyStateAdded.current = false;
    };
  }, [message, showDialog, pendingLocation]);

  return children;
};

const NavigationDialog: React.FC<{
  message: string;
}> = ({ message }) => {
  const context = useContext(NavigationBlockerContext);
  Assert.assert(context !== null);

  const { showDialog, pendingLocation } = context;
  const showDialogValue = useWatchableValue(showDialog);
  const { close } = useTabManager();
  const { tabId } = useTabParams<{ tabId: string }>();

  const handleConfirm = () => {
    const dialog = showDialog.snapshot();
    showDialog.set(null);

    pendingLocation.set('back');
    // Allow the back navigation to proceed
    if (dialog === 'back') {
      window.history.back();
    }
    if (dialog === 'close') {
      close(tabId);
    }
  };

  const handleCancel = () => {
    showDialog.set(null);
    pendingLocation.set(null);
  };

  return (
    <Modal open={showDialogValue !== null} onClose={() => handleCancel()}>
      <ModalPageView title="Confirm Navigation" className="p-4">
        <div className="flex flex-col gap-4">
          <Text.P>{message}</Text.P>
          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel}>Stay</Button>
            <Button onClick={handleConfirm} variant="destructive">
              Leave
            </Button>
          </div>
        </div>
      </ModalPageView>
    </Modal>
  );
};

const NavigationBlockerContext = createContext<{
  showDialog: Watchable<'back' | 'close' | null>;
  pendingLocation: Watchable<'back' | null>;
} | null>(null);

const message = 'You have unsaved changes. Are you sure you want to leave?';

export const AppTab: React.FC<{
  model: {
    changes: ReadonlyWatchable<Record<string, boolean | string>>;
    route?: ReadonlyWatchable<string>;
    pageTitle: ReadonlyWatchable<string>;
  } & TargetableModel;
  children: React.ReactNode;
}> = ({ model, children }) => {
  const ctx = useMemo(
    () => ({
      showDialog: Watchable.fromValue<'back' | 'close' | null>(null),
      pendingLocation: Watchable.fromValue<'back' | null>(null),
    }),
    []
  );

  const getHasChanges = useCallback(() => {
    return Object.values(model.changes.snapshot()).some((v) => v);
  }, [model.changes]);

  return (
    <NavigationBlockerContext.Provider value={ctx}>
      <TabIndicator
        model={model}
        onClose={() => {
          const shouldClose = !getHasChanges();

          if (!shouldClose) {
            ctx.showDialog.set('close');
          }

          return shouldClose;
        }}
      />
      <NavigationBlocker shouldBlock={() => getHasChanges()} message={message}>
        {children}
      </NavigationBlocker>
      <NavigationDialog message={message} />
    </NavigationBlockerContext.Provider>
  );
};
